import { useId, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthContext";
import { RenameButton } from "@/components/RenameButton";
import { useWorkspaces } from "@/features/workspaces/useWorkspaces";
import { ApiError } from "@/lib/http";
import { navigationError } from "./api";
import { useNavigation } from "./useNavigation";
import { NAVIGATION_ICONS, type NavigationConfig, type NavigationGroup, type NavigationIcon } from "./types";
import "./NavigationSettings.css";

const iconLabels: Record<NavigationIcon, string> = {
  layers: "Área", factory: "Produção", flask: "Processo", flame: "Vapor e calor",
  wind: "Ar", water: "Água", energy: "Energia",
};

function move<T>(items: T[], index: number, offset: number): T[] {
  const next = [...items];
  const target = index + offset;
  if (target < 0 || target >= next.length) return next;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** Organizacao compartilhada; favoritos e recolhimento pertencem a cada usuario. */
export function NavigationSettings() {
  const { aberto, permissoes } = useAuth();
  const { config, status, error, reload, save } = useNavigation();
  const workspaces = useWorkspaces();
  const [draft, setDraft] = useState<NavigationConfig | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const saving = useRef(false);
  const formId = useId();
  if (aberto || !permissoes.gerenciarWorkspaces) return null;
  const current = draft ?? config;
  const busy = status === "saving";
  const changedElsewhere = !!draft && !!config && draft.revision !== config.revision;
  const names = new Map(workspaces.workspaces.map(workflow => [workflow.id, workflow.name]));

  function change(groups: NavigationGroup[]) {
    if (!current) return;
    setDraft({ ...current, groups });
    setMessage(null);
    setFormError(null);
  }

  function updateGroup(id: string, changes: Partial<NavigationGroup>) {
    if (current) change(current.groups.map(group => group.id === id ? { ...group, ...changes } : group));
  }

  function assignWorkflow(workflowId: string, groupId: string) {
    if (!current) return;
    change(current.groups.map(group => ({ ...group,
      workflowIds: [...group.workflowIds.filter(id => id !== workflowId), ...(group.id === groupId ? [workflowId] : [])],
    })));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!draft || saving.current || conflict || changedElsewhere) return;
    if (draft.groups.some(group => !group.name.trim())) {
      setFormError("Informe o nome de todas as áreas.");
      return;
    }
    saving.current = true;
    setFormError(null);
    setMessage(null);
    try {
      await save({ ...draft, groups: draft.groups.map(group => ({ ...group,
        name: group.name.trim(), section: group.section?.trim() || null,
      })) });
      setDraft(null);
      setMessage("Organização do menu salva para toda a equipe.");
    } catch (cause) {
      setConflict(cause instanceof ApiError && cause.status === 409);
      setFormError(navigationError(cause));
    } finally { saving.current = false; }
  }

  function discard() {
    setDraft(null); setConflict(false); setFormError(null); setMessage(null); reload();
  }

  return <section id="navegacao" className="navigation-settings" aria-labelledby={`${formId}-title`}>
    <header className="navigation-settings-heading">
      <div><h2 id={`${formId}-title`}>Áreas e equipamentos</h2>
        <p>Organize o menu para toda a equipe. Favoritos e áreas recolhidas continuam pessoais.</p></div>
    </header>
    {!current ? <div>
      {status === "error" ? <><p role="alert">{error}</p><button className="btn" type="button" onClick={reload}>Tentar carregar áreas novamente</button></>
        : <p>Carregando organização do menu...</p>}
    </div> : <form onSubmit={event => void submit(event)}>
      <p className="navigation-settings-hint">Uma área reúne equipamentos. A seção é opcional: use termos da sua planta, como Produção ou Utilidades.</p>
      {conflict || changedElsewhere ? <div className="navigation-settings-notice" role="alert">
        <p>Outra pessoa alterou a organização do menu. Seu rascunho foi preservado e não pode sobrescrever a versão nova.</p>
        <button className="btn" type="button" disabled={busy} onClick={discard}>Descartar rascunho e recarregar</button>
      </div> : null}
      {current.groups.length === 0 ? <p className="navigation-settings-empty">Nenhuma área cadastrada. Os equipamentos continuam acessíveis no menu.</p> : null}
      <div className="navigation-groups-editor">
        {current.groups.map((group, index) => <fieldset className="navigation-group-editor" key={group.id} disabled={busy}>
          <legend>{group.name.trim() || `Nova área ${index + 1}`}</legend>
          <div className="navigation-group-fields">
            <label>Nome da área<input className="input" aria-label={`Nome da área ${index + 1}`} maxLength={80} required
              value={group.name} onChange={event => updateGroup(group.id, { name: event.target.value })} /></label>
            <label>Seção opcional<input className="input" aria-label={`Seção da área ${index + 1}`} maxLength={80}
              placeholder="Ex.: Produção" value={group.section ?? ""}
              onChange={event => updateGroup(group.id, { section: event.target.value || null })} /></label>
            <label>Ícone<select className="input" aria-label={`Ícone da área ${index + 1}`} value={group.icon}
              onChange={event => updateGroup(group.id, { icon: event.target.value as NavigationIcon })}>
              {NAVIGATION_ICONS.map(icon => <option value={icon} key={icon}>{iconLabels[icon]}</option>)}
            </select></label>
          </div>
          <div className="navigation-group-actions">
            <button className="btn sm" type="button" disabled={index === 0} onClick={() => change(move(current.groups, index, -1))}
              aria-label={`Mover área ${group.name || index + 1} para cima`}>↑ Subir área</button>
            <button className="btn sm" type="button" disabled={index === current.groups.length - 1} onClick={() => change(move(current.groups, index, 1))}
              aria-label={`Mover área ${group.name || index + 1} para baixo`}>↓ Descer área</button>
            <button className="btn ghost sm" type="button" onClick={() => change(current.groups.filter(item => item.id !== group.id))}
              aria-label={`Remover área ${group.name || index + 1}`}>Remover área</button>
            <span>Remover a área apenas desagrupa seus equipamentos.</span>
          </div>
          {group.workflowIds.length ? <ol className="navigation-group-equipment">
            {group.workflowIds.map((workflowId, position) => <li key={workflowId}>
              <span>{names.get(workflowId) ?? "Equipamento indisponível"}</span>
              <div>
                <button className="btn ghost sm" type="button" disabled={position === 0}
                  aria-label={`Mover ${names.get(workflowId) ?? "equipamento"} para cima`}
                  onClick={() => updateGroup(group.id, { workflowIds: move(group.workflowIds, position, -1) })}>↑</button>
                <button className="btn ghost sm" type="button" disabled={position === group.workflowIds.length - 1}
                  aria-label={`Mover ${names.get(workflowId) ?? "equipamento"} para baixo`}
                  onClick={() => updateGroup(group.id, { workflowIds: move(group.workflowIds, position, 1) })}>↓</button>
              </div>
            </li>)}
          </ol> : <p className="navigation-settings-hint">Associe equipamentos a esta área na lista abaixo.</p>}
        </fieldset>)}
      </div>
      <button className="btn" type="button" disabled={busy || current.groups.length >= 64} onClick={() => change([...current.groups,
        { id: `area-${crypto.randomUUID()}`, name: "", section: null, icon: "layers", workflowIds: [] },
      ])}>Adicionar área</button>
      <fieldset className="navigation-assignments" disabled={busy || workspaces.carregando}>
        <legend>Equipamentos em cada área</legend>
        {workspaces.erro ? <p role="alert">Não foi possível atualizar os equipamentos. <button type="button" className="btn sm" onClick={workspaces.recarregar}>Tentar novamente</button></p> : null}
        {workspaces.carregando ? <p>Carregando equipamentos...</p> : null}
        {!workspaces.carregando && !workspaces.workspaces.length ? <p>Cadastre o primeiro equipamento no bloco abaixo.</p> : null}
        {workspaces.workspaces.map(workflow => <label className="navigation-assignment" key={workflow.id}>
          <span>{workflow.name}</span>
          <select className="input" aria-label={`Área de ${workflow.name}`}
            value={current.groups.find(group => group.workflowIds.includes(workflow.id))?.id ?? ""}
            onChange={event => assignWorkflow(workflow.id, event.target.value)}>
            <option value="">Sem área</option>
            {current.groups.map((group, index) => <option key={group.id} value={group.id}>{group.name.trim() || `Nova área ${index + 1}`}</option>)}
          </select>
        </label>)}
      </fieldset>
      {formError && !conflict ? <p role="alert" className="navigation-settings-error">{formError}</p> : null}
      {error && !formError && !conflict ? <p role="alert" className="navigation-settings-error">{error}</p> : null}
      {message ? <p role="status" className="navigation-settings-success">{message}</p> : null}
      <div className="navigation-save-actions">
        <button className="btn primary" type="submit" disabled={!draft || busy || conflict || changedElsewhere}>{busy ? "Salvando áreas..." : "Salvar organização"}</button>
        <button className="btn" type="button" disabled={!draft || busy} onClick={discard}>Cancelar alterações</button>
        {draft ? <span>Alterações ainda não salvas.</span> : null}
      </div>
    </form>}
    <WorkflowCatalog workspaces={workspaces} hasDraft={draft !== null} />
  </section>;
}

function WorkflowCatalog({ workspaces, hasDraft }: { workspaces: ReturnType<typeof useWorkspaces>; hasDraft: boolean }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!name.trim() || submitting.current) return;
    submitting.current = true; setBusy(true); setError(null); setMessage(null);
    try {
      const created = await workspaces.criar(name.trim(), description.trim());
      setName(""); setDescription(""); setMessage(`${created.name} cadastrado. Associe-o a uma área acima.`);
    } catch { setError("Não foi possível cadastrar o equipamento. Tente novamente."); }
    finally { submitting.current = false; setBusy(false); }
  }

  async function remove(id: string) {
    if (hasDraft || submitting.current) return;
    submitting.current = true; setBusy(true); setError(null); setMessage(null);
    try {
      await workspaces.remover(id);
      setPendingRemoval(null); setMessage("Equipamento removido. Seus dispositivos voltaram para descobertos.");
    } catch { setError("Não foi possível confirmar a remoção. Atualize a lista antes de tentar novamente."); workspaces.recarregar(); }
    finally { submitting.current = false; setBusy(false); }
  }

  return <details className="navigation-catalog">
    <summary>Cadastro de equipamentos</summary>
    <p>Cadastros e nomes são salvos imediatamente. Eles não são desfeitos ao cancelar a organização do menu.</p>
    <form className="navigation-create-workflow" onSubmit={event => void create(event)}>
      <label>Nome do equipamento<input className="input" required maxLength={80} disabled={busy} value={name} onChange={event => setName(event.target.value)} /></label>
      <label>Descrição opcional<input className="input" maxLength={240} disabled={busy} value={description} onChange={event => setDescription(event.target.value)} /></label>
      <button className="btn" type="submit" disabled={busy || !name.trim()}>Cadastrar equipamento</button>
    </form>
    <ul className="navigation-catalog-list">
      {workspaces.workspaces.map(workflow => <li key={workflow.id}>
        <div className="navigation-catalog-name"><Link to={`/workflows/${encodeURIComponent(workflow.id)}`}>{workflow.name}</Link>
          <RenameButton label={`Renomear equipamento ${workflow.name}`} value={workflow.name}
            help="O endereço, os dispositivos e o histórico continuam os mesmos." onSave={value => workspaces.renomear(workflow.id, value)} /></div>
        <div className="navigation-catalog-actions">
          <Link to={`/workflows/${encodeURIComponent(workflow.id)}?addDevice=1`}>Adicionar dispositivo</Link>
          <Link to={`/workspaces/${encodeURIComponent(workflow.id)}/devices`}>Ver dispositivos</Link>
          <button className="btn ghost sm" type="button" disabled={busy || hasDraft} onClick={() => setPendingRemoval(workflow.id)}
            title={hasDraft ? "Salve ou cancele a organização antes de remover um equipamento." : undefined}>Remover {workflow.name}</button>
        </div>
        {pendingRemoval === workflow.id ? <div className="navigation-settings-notice" role="group" aria-label={`Confirmar remoção de ${workflow.name}`}>
          <p>Remover o equipamento {workflow.name}? Seus dispositivos serão desassociados e voltarão para descobertos. Essa ação remove o cadastro, não apenas a área do menu.</p>
          <button type="button" className="btn" disabled={busy || hasDraft} onClick={() => void remove(workflow.id)}>Confirmar remoção do equipamento</button>
          <button type="button" className="btn" disabled={busy} onClick={() => setPendingRemoval(null)}>Manter equipamento</button>
        </div> : null}
      </li>)}
    </ul>
    {error ? <p role="alert" className="navigation-settings-error">{error}</p> : null}
    {message ? <p role="status" className="navigation-settings-success">{message}</p> : null}
  </details>;
}
